import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import http from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { STATUS_RU } from "@/lib/statuses";
import { fmt } from "./AdminKit";
import { ScanLine } from "lucide-react";

const money = (n) => `${Number(n || 0).toLocaleString("nb-NO")} kr`;

export default function AdminScan() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(codeParam || "");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState("admin");

  useEffect(() => { setRole(localStorage.getItem("admin_role") || "admin"); }, []);

  const resolve = async (c) => {
    const val = (c ?? code).trim();
    if (!val) return;
    setLoading(true); setRes(null);
    try {
      const r = await http.get(`/v1/admin/scan/${encodeURIComponent(val)}`);
      setRes(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Код не найден");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (codeParam) resolve(codeParam); /* eslint-disable-next-line */ }, [codeParam]);

  const order = res?.order;
  const b = order?.billing || {};
  const outstanding = Math.max(0, Number(b.final_total_incl_mva || 0) - Number(b.amount_paid || 0));

  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-scan">
      <h1 className="text-xl font-bold text-white flex items-center gap-2"><ScanLine className="h-5 w-5" />Сканировать / Поиск по коду</h1>
      <p className="text-sm text-slate-400">Отсканируйте QR-наклейку телефоном или введите код машины (MCH-…) либо заказа (BKT-…).</p>
      <form onSubmit={(e) => { e.preventDefault(); resolve(); }} className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="MCH-0001 или BKT-2026-0001"
          className="bg-slate-700 border-slate-600 text-white font-mono" data-testid="scan-input" autoFocus />
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="scan-submit">{loading ? "..." : "Найти"}</Button>
      </form>

      {res && (
        <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-4 space-y-3" data-testid="scan-result">
          <div className="flex flex-wrap items-center gap-2">
            {res.machine?.machine_code && <span className="px-2 py-0.5 rounded bg-indigo-900/50 text-indigo-100 border border-indigo-700/50 font-mono text-xs">{res.machine.machine_code}</span>}
            {order?.order_number && <span className="px-2 py-0.5 rounded bg-blue-900/40 text-blue-200 border border-blue-700/50 font-mono text-xs">{order.order_number}</span>}
            {order && <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-200 text-xs">{STATUS_RU[order.status] || order.status}</span>}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-slate-400">Машина:</span> <span className="text-slate-100">{[res.machine?.brand, res.machine?.model].filter(Boolean).join(" ") || order?.machine_brand || "—"}</span></div>
            <div><span className="text-slate-400">Клиент:</span> <span className="text-slate-100">{res.customer?.name || order?.customer_name || "—"}</span></div>
            <div><span className="text-slate-400">Телефон:</span> <span className="text-slate-100">{res.customer?.phone || order?.phone || "—"}</span></div>
            <div><span className="text-slate-400">Серийный №:</span> <span className="text-slate-100">{res.machine?.serial_number || "—"}</span></div>
          </div>

          {order && (
            <div className="rounded border border-slate-700 bg-slate-900/40 p-3">
              <p className="text-sm text-slate-200 font-semibold mb-1">Выдача / Betaling</p>
              <div className="text-xs text-slate-300 space-y-0.5">
                <div>Статус оплаты: <span className="text-slate-100">{b.payment_status || "ubetalt"}</span></div>
                <div>Итого (вкл. НДС): <span className="text-slate-100">{money(b.final_total_incl_mva)}</span></div>
                <div>Оплачено: <span className="text-slate-100">{money(b.amount_paid)}</span></div>
                <div className={outstanding > 0 ? "text-amber-300 font-semibold" : "text-green-300 font-semibold"}>К оплате: {money(outstanding)}</div>
              </div>
            </div>
          )}

          {res.history?.length > 1 && (
            <div>
              <p className="text-xs text-slate-400 mb-1">История машины ({res.history.length}):</p>
              <ul className="space-y-1">
                {res.history.map((h) => (
                  <li key={h.id} className="text-xs text-slate-300 flex flex-wrap gap-2">
                    <span className="font-mono text-indigo-200">{h.order_number}</span>
                    <span className="text-slate-400">{STATUS_RU[h.status] || h.status}</span>
                    <span className="text-slate-600">{fmt(h.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            {role === "admin" && order && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate(`/admin/reparasjoner?open=${order.id}`)} data-testid="scan-open-order">Открыть в ремонтных заказах</Button>
            )}
            {role === "admin" && res.machine && (
              <Button size="sm" variant="outline" className="border-indigo-600 text-indigo-200" onClick={() => navigate("/admin/mashiny")} data-testid="scan-open-machine">Открыть машину</Button>
            )}
          </div>
          {role === "workshop" && (
            <p className="text-xs text-slate-500">Режим приёмки: виден статус и сумма к оплате. Внутренние цены и поставщики скрыты.</p>
          )}
        </div>
      )}
    </div>
  );
}
